import library from '@/modules/library/library'
import { getInstance } from '@/plugins/auth0'

// Store functionality
import actions from '@/store/actions'
import getters from '@/store/getters'
import modules from '@/store/modules'
import { Tenant, TenantToken } from '@/store/modules/app/types'
import mutations from '@/store/mutations'
import state from '@/store/state'
import { TenantHelpers } from '@/utils/tenant-helpers'
import { HelpHero } from 'helphero'

/**
 * Vuex
 *
 * @library
 *
 * https://vuex.vuejs.org/en/
 */
// Lib imports
import Vue from 'vue'
import Vuex from 'vuex'

declare var consoleLog: any

Vue.use(Vuex)

// devtools true set before the new Vuex.Store allows the vue extension to inspect the state in Chrome.
Vue.config.devtools = process.env.VUE_APP_DEV_TOOLS === 'true'

modules[library.moduleName] = library

// store function that can be shared among store modules.
const storePlugins = (store: any) => {
  store.getMyTenantID = (appState: any): number => {
    if (appState.myTenantIndex !== TenantHelpers.InvalidTenantIndex) {
      const myTenant = appState.myTenants[appState.myTenantIndex] as Tenant
      if (myTenant !== null && myTenant.ID > 0) {
        return myTenant.ID
      }
    }

    return TenantHelpers.InvalidTenantID
  }

  store.setMyTenantIndex = (appState: any, tenantIndex: number) => {
    let resetLibraryDataRequired = false

    if (tenantIndex < appState.myTenants.length) {
      const prevTenantIndex = appState.myTenantIndex
      appState.myTenantIndex = tenantIndex

      sessionStorage.setItem('myTenantIndex', appState.myTenantIndex)
      if (prevTenantIndex !== tenantIndex) {
        // When the tenant index changes, make sure we're not hanging onto the previous tenants appState.
        resetLibraryDataRequired = true
      }
      appState.myTenant = appState.myTenants[appState.myTenantIndex]
      appState.myTenantToken = appState.myTenantTokens.find((token: TenantToken) => token.id === appState.myTenant.ID)
    } else {
      appState.myTenant = TenantHelpers.GetDefaultTenant()
      appState.myTenantToken = TenantHelpers.GetDefaultTenantToken()
    }

    // reset Jig Count and Jig List pagination data
    store.commit('jig/resetTenantJigCountsAndPagination', null, {
      root: true,
    })
    store.commit('jig/updateLockPayloadTenantId', appState.myTenant.ID, { root: true })

    return resetLibraryDataRequired
  }

  store.updateHelpHeroData = (key: string, value: any) => {
    if (!Vue.prototype.$hlp) {
      consoleLog('!HelpHero is not ready')
      return
    }
    const data = {} as any
    data[key] = value
    ;(Vue.prototype.$hlp as HelpHero).update(data)
    consoleLog(`Updated HelpHero with data: ${JSON.stringify(data)}`)
  }

  store.resetLibraryData = () => {
    store.commit('teamlibrary/resetLibraryData', null, {
      root: true,
    })
  }

  store.authService = () => getInstance()
}

const store = new Vuex.Store({
  actions,
  getters,
  mutations,
  state,
  modules,
  plugins: [storePlugins],
})

export default store
